// Live Preview publisher — pushes the presentation half of the branch being
// edited (images, imagePacks, constructionImagePacks, sounds, styles and
// localization) to the user's own running game sessions via the relay's
// /RiftTWS channel, in real time. Logic fields are never serialized, so a push
// cannot touch game rules or multiplayer state. Every push names the branch,
// and a game applies it only while running that branch.
//
// Wire semantics: every push carries the FULL working art dictionaries and
// styles as `upserts`, plus a CUMULATIVE `deletions` list per dict —
// (baseline keys ∪ every key pushed this session) − current keys — so any
// single message completely describes the working copy relative to anything a
// receiver could hold. Localization is too large to resend whole on every
// keystroke, and a game may run an older version whose text describes its own
// rules, so it goes as a difference from the loaded branch instead: every key
// whose text differs from it, plus every key pushed before (so an edit that is
// reverted is reverted in-game too), with the same cumulative deletions.
// Pushes are idempotent and order-insensitive; late joiners are corrected by
// the next push (and we push immediately when presence reports a new one).
// The frame `type` is still 'theme': that is what the relays forward.
//
// AUTH NOTE (Phase 4): the uid is currently sent as an unverified query param
// — local/dev relay use only until the token handshake lands.
import {ImageMetadata, RemoteConstantsModel} from "../models/remoteConstants.model";
import {computePixelMultiplier, loadNaturalWidth} from "./exportTransform";
import {apiBindings, LIVE_PREVIEW_DEBOUNCE_MS} from "./constants";

// Leading-edge + throttle (not a trailing debounce): an isolated edit pushes
// IMMEDIATELY — in-game latency is just the wire — while a burst (slider
// drag, bulk paste) coalesces to one push per interval plus a trailing push
// for the final state. The receiver applies only the newest message per
// frame, so burst pushes can't pile up on the game side.
const PUSH_MIN_INTERVAL_MS = LIVE_PREVIEW_DEBOUNCE_MS;
const RECONNECT_MS = 3000;       // initial per-relay retry; doubles per failure
const RECONNECT_MAX_MS = 60000;  // so an absent relay (e.g. no local one) stays quiet
const RELAY_STORAGE_KEY = 'livePreviewRelay';

// Every relay a game build might be listening on — one per profile in
// Admin/PlayFabConfig/playfab_config.json (local/debug/release).
// The game's build profile decides its relay and the editor cannot know
// which build the user is running, so the publisher connects to ALL of
// them and pushes to whichever accept. Rooms are uid-keyed per relay and
// pushes are idempotent full-state, so publishing where nobody listens is
// harmless. ws://localhost works even from the https editor: browsers
// treat localhost as a potentially-trustworthy origin, exempt from
// mixed-content blocking.
const DEFAULT_RELAYS = [
    'ws://localhost:8080',
    'wss://rift-ws-debug.azurewebsites.net',
    'wss://rift-ws-release.azurewebsites.net',
];

const ART_DICTS = ['images', 'imagePacks', 'constructionImagePacks', 'sounds'] as const;
type ArtDict = typeof ART_DICTS[number];

type Localization = RemoteConstantsModel['localization'];

const hasKey = (table: object, key: string) => Object.prototype.hasOwnProperty.call(table, key);

export interface RelayStatus {
    url: string;
    label: string;       // short name for the UI: local | debug | release | hostname
    connected: boolean;
    receivers: number;
}

export interface LivePreviewStatus {
    enabled: boolean;
    connected: boolean;  // at least one relay connected
    receivers: number;   // sum across relays
    relays: RelayStatus[];
    lastPushSeq: number;
    // Edits since the baseline that did not change what a push carries — i.e.
    // logic changes a live push cannot preview (they need an export).
    logicChanges: number;
    uid: string;
    // The branch pushes are for.
    branch: string;
}

function relayUrls(): string[] {
    // Escape hatch: a comma-separated list in localStorage overrides the
    // defaults (single URL still works — it's a one-element list).
    const stored = localStorage.getItem(RELAY_STORAGE_KEY);
    return stored ? stored.split(',').map(s => s.trim()).filter(Boolean) : DEFAULT_RELAYS;
}

function relayLabel(wsUrl: string): string {
    try {
        const host = new URL(wsUrl).hostname;
        if (host === 'localhost' || host === '127.0.0.1') return 'local';
        if (host.includes('debug')) return 'debug';
        if (host.includes('release')) return 'release';
        return host;
    } catch {
        return wsUrl;
    }
}

interface RelayConnection {
    url: string;
    label: string;
    ws: WebSocket | null;
    receivers: number;
    retryMs: number;
    reconnectTimer: number | undefined;
}

function emptyKeySets(): Record<ArtDict, Set<string>> {
    return {images: new Set(), imagePacks: new Set(), constructionImagePacks: new Set(), sounds: new Set()};
}

// The client renders from pixelMultiplier (PPU); `scale` is an editor-side
// authoring knob baked into pixelMultiplier at export. Live pushes must bake
// too or scale edits appear to do nothing in-game. Lenient counterpart of
// bakeImagePixelMultipliers: never throws (a preview push shouldn't block
// like an export does). Width sources, in order: stored srcWidth (post-
// v0.4.26 uploads), this session's width cache, then a one-time network
// dimensions read exactly like the export bake — so legacy images preview
// correctly after the first push warms the cache. Unresolvable entries pass
// through with their stored pixelMultiplier.
const naturalWidthCache = new Map<string, number>(); // fileName → px width

async function bakeLenient(images: {[key: string]: ImageMetadata}): Promise<{[key: string]: ImageMetadata}> {
    const out: {[key: string]: ImageMetadata} = {};
    const pending: Promise<void>[] = [];

    for (const key of Object.keys(images ?? {})) {
        const entry = images[key];
        if (!entry || typeof entry !== 'object' || !entry.fileName) {
            out[key] = entry;
            continue;
        }

        const width = entry.srcWidth ?? naturalWidthCache.get(entry.fileName);
        if (width) {
            out[key] = {...entry, pixelMultiplier: computePixelMultiplier(width, entry.scale)};
            continue;
        }

        out[key] = entry; // provisional — replaced below if the width resolves
        const fileName = entry.fileName;
        pending.push(loadNaturalWidth(`${apiBindings.images}${fileName}`).then(loaded => {
            if (!loaded) return;
            naturalWidthCache.set(fileName, loaded);
            out[key] = {...entry, pixelMultiplier: computePixelMultiplier(loaded, entry.scale)};
        }));
    }

    if (pending.length > 0) await Promise.all(pending);
    return out;
}

class LivePreviewPublisher {
    private relays: RelayConnection[] = [];
    private listeners = new Set<() => void>();
    private status: LivePreviewStatus = {
        enabled: false,
        connected: false,
        receivers: 0,
        relays: [],
        lastPushSeq: 0,
        logicChanges: 0,
        // The room key is ALWAYS the logged-in editor account's own PlayFabId,
        // supplied by the verified session (setUid, fed from /me). Never a
        // free-text or persisted value — "you can only see yourself".
        uid: '',
        branch: '',
    };

    // Union of baseline keys + every key pushed this broadcast session.
    private pushedKeys: Record<ArtDict, Set<string>> = emptyKeySets();
    // Localization as the branch loaded (frame 0), which text is pushed as a difference
    // from, and every key pushed per language. The pushed keys outlive a re-baseline and
    // are only forgotten with the branch: undoing back to frame 0 must still send the
    // reverted text to a game that took the edit.
    private loadedLocalization: Localization = {};
    private pushedLocalizationKeys: Record<string, Set<string>> = {};
    private latestState: RemoteConstantsModel | null = null;
    private lastSubsetJson = '';
    private seq = 0;
    private pushGeneration = 0;
    private throttleTimer: number | undefined;
    private lastPushSentAt = 0;

    // --- useSyncExternalStore surface ---
    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };
    getSnapshot = (): LivePreviewStatus => this.status;

    private update(partial: Partial<LivePreviewStatus>) {
        this.status = {...this.status, ...partial};
        this.listeners.forEach(l => l());
    }

    // --- control surface ---
    // Set the room key from the verified session identity (the editor account's
    // own PlayFabId). Called with '' on logout to drop the room. Not persisted.
    setUid(uid: string) {
        if (uid === this.status.uid) return;
        this.update({uid});
        if (this.status.enabled) this.connect(); // rejoin the right room (or disconnect if '')
    }

    setEnabled(enabled: boolean) {
        if (enabled === this.status.enabled) return;
        this.update({enabled});
        if (enabled) {
            this.resetSession();
            this.connect();
        } else {
            this.disconnect();
        }
    }

    // Called by IntegratorProvider on every state identity or branch change.
    // frame === 0 marks a fresh baseline (branch load / history reset); a new
    // branch name with the same state is New Branch renaming the working copy.
    onStateChanged(state: RemoteConstantsModel, frame: number, branch: string) {
        this.latestState = state;
        const branchChanged = branch !== this.status.branch;
        if (branchChanged) {
            this.pushedLocalizationKeys = {};
            this.update({branch});
        }
        // Tracked whether or not broadcasting is on: turning it on later must still
        // diff text against what was loaded, not against edits made since. A deep
        // copy, since commands may edit the tables in place.
        if ((frame === 0 || branchChanged) && state?.localization)
            this.loadedLocalization = JSON.parse(JSON.stringify(state.localization));

        if (!this.status.enabled) return;
        if (!state || !state.images) return; // pre-load empty state

        if (frame === 0 || branchChanged) {
            this.resetSession();
            this.pushNow(); // self-heal receivers that hold stale preview
            return;
        }

        const subsetJson = this.serializeSubset(state);
        if (subsetJson === this.lastSubsetJson) {
            // The edit didn't touch anything a push carries → not previewable live.
            this.update({logicChanges: this.status.logicChanges + 1});
            return;
        }

        // First edit after a quiet spell goes out right now; edits inside the
        // throttle window schedule a trailing push at the window's end.
        window.clearTimeout(this.throttleTimer);
        const wait = PUSH_MIN_INTERVAL_MS - (Date.now() - this.lastPushSentAt);
        if (wait <= 0)
            this.pushNow();
        else
            this.throttleTimer = window.setTimeout(() => this.pushNow(), wait);
    }

    async pushNow() {
        window.clearTimeout(this.throttleTimer);
        const state = this.latestState;
        if (!state || !state.images || !this.anyOpen()) return;

        // The bake can await network width-reads on the first push of a
        // session. A push that was overtaken while awaiting must not send —
        // its snapshot is older than what's already gone out.
        const generation = ++this.pushGeneration;
        const bakedImages = await bakeLenient(state.images);
        if (generation !== this.pushGeneration) return;
        if (!this.anyOpen()) return;

        const deletions: Record<string, unknown> = {};
        for (const dict of ART_DICTS) {
            const current = state[dict] ?? {};
            deletions[dict] = Array.from(this.pushedKeys[dict]).filter(key => !(key in current));
            // Deleted keys stay in pushedKeys: they must keep appearing in
            // deletions so late-joining receivers also drop them.
            Object.keys(current).forEach(key => this.pushedKeys[dict].add(key));
        }
        const text = this.localizationChanges(state);
        deletions.localization = text.deletions;

        this.seq += 1;
        const message = {
            type: 'theme',
            seq: this.seq,
            branch: this.status.branch,
            upserts: {
                images: bakedImages,
                imagePacks: state.imagePacks,
                constructionImagePacks: state.constructionImagePacks,
                sounds: state.sounds,
                styles: state.styles,
                localization: text.upserts,
            },
            deletions,
        };

        // Same frame to every connected relay. Relays where the receivers
        // are already current just apply an identical state — idempotent.
        const json = JSON.stringify(message);
        for (const relay of this.relays)
            if (relay.ws?.readyState === WebSocket.OPEN) relay.ws.send(json);

        this.lastPushSentAt = Date.now();
        this.lastSubsetJson = this.serializeSubset(state);
        this.update({lastPushSeq: this.seq});
    }

    // --- internals ---
    private serializeSubset(state: RemoteConstantsModel): string {
        return JSON.stringify({
            images: state.images,
            imagePacks: state.imagePacks,
            constructionImagePacks: state.constructionImagePacks,
            sounds: state.sounds,
            styles: state.styles,
            localization: state.localization,
        });
    }

    // Text as a difference from the loaded branch (see the header): every key whose
    // text differs from it or that was pushed before, and every loaded or pushed key
    // that is gone. Deleted keys stay in pushedLocalizationKeys so they keep
    // appearing in deletions for late joiners, as with the art dictionaries.
    private localizationChanges(state: RemoteConstantsModel): {upserts: Localization, deletions: Record<string, string[]>} {
        const current = state.localization ?? {};
        const loaded = this.loadedLocalization;
        const upserts: Localization = {};
        const deletions: Record<string, string[]> = {};
        const languages = new Set([...Object.keys(current), ...Object.keys(loaded), ...Object.keys(this.pushedLocalizationKeys)]);

        for (const language of Array.from(languages)) {
            const now = current[language] ?? {};
            const before = loaded[language] ?? {};
            const pushed = this.pushedLocalizationKeys[language] ??= new Set<string>();

            const changed: {[key: string]: string} = {};
            for (const key of Object.keys(now))
                if (now[key] !== before[key] || pushed.has(key)) changed[key] = now[key];
            const gone = new Set([...Object.keys(before), ...Array.from(pushed)].filter(key => !hasKey(now, key)));

            Object.keys(changed).forEach(key => pushed.add(key));
            if (Object.keys(changed).length > 0) upserts[language] = changed;
            if (gone.size > 0) deletions[language] = Array.from(gone);
        }
        return {upserts, deletions};
    }

    private resetSession() {
        this.pushedKeys = emptyKeySets();
        const state = this.latestState;
        if (state) {
            for (const dict of ART_DICTS)
                Object.keys(state[dict] ?? {}).forEach(key => this.pushedKeys[dict].add(key));
            this.lastSubsetJson = this.serializeSubset(state);
        } else {
            this.lastSubsetJson = '';
        }
        this.update({logicChanges: 0});
    }

    private anyOpen(): boolean {
        return this.relays.some(r => r.ws?.readyState === WebSocket.OPEN);
    }

    private connect() {
        this.disconnectSocketsOnly();
        if (!this.status.uid) return; // control surfaces the missing-uid state

        this.relays = relayUrls().map(url => ({
            url,
            label: relayLabel(url),
            ws: null,
            receivers: 0,
            retryMs: RECONNECT_MS,
            reconnectTimer: undefined,
        }));
        this.relays.forEach(relay => this.open(relay));
        this.publishRelayStatus();
    }

    private open(relay: RelayConnection) {
        const url = `${relay.url}/RiftTWS?uid=${encodeURIComponent(this.status.uid)}&role=publisher`;
        let ws: WebSocket;
        try {
            ws = new WebSocket(url);
        } catch {
            // Malformed URL (only possible via the localStorage override) —
            // it will never get better, so no retry; other relays still run.
            return;
        }
        relay.ws = ws;

        ws.onopen = () => {
            if (relay.ws !== ws) return;
            relay.retryMs = RECONNECT_MS;
            this.publishRelayStatus();
            this.pushNow(); // receivers on this relay may have been waiting
        };
        ws.onmessage = event => {
            if (relay.ws !== ws) return;
            try {
                const msg = JSON.parse(event.data as string);
                if (msg?.type === 'presence' && typeof msg.receivers === 'number') {
                    const joined = msg.receivers > relay.receivers;
                    relay.receivers = msg.receivers;
                    this.publishRelayStatus();
                    if (joined) this.pushNow(); // bring the newcomer up to date
                }
            } catch {
                // Non-JSON frames are not part of the protocol — ignore.
            }
        };
        ws.onclose = () => {
            if (relay.ws !== ws) return;
            relay.ws = null;
            relay.receivers = 0;
            this.publishRelayStatus();
            if (this.status.enabled) {
                window.clearTimeout(relay.reconnectTimer);
                relay.reconnectTimer = window.setTimeout(() => this.open(relay), relay.retryMs);
                relay.retryMs = Math.min(relay.retryMs * 2, RECONNECT_MAX_MS);
            }
        };
    }

    private publishRelayStatus() {
        const relays = this.relays.map(r => ({
            url: r.url,
            label: r.label,
            connected: r.ws?.readyState === WebSocket.OPEN,
            receivers: r.receivers,
        }));
        this.update({
            relays,
            connected: relays.some(r => r.connected),
            receivers: relays.reduce((sum, r) => sum + r.receivers, 0),
        });
    }

    private disconnectSocketsOnly() {
        for (const relay of this.relays) {
            window.clearTimeout(relay.reconnectTimer);
            const ws = relay.ws;
            relay.ws = null; // handlers guard on identity → close becomes inert
            if (ws) {
                try { ws.close(); } catch { /* already closed */ }
            }
        }
        this.relays = [];
    }

    private disconnect() {
        this.disconnectSocketsOnly();
        window.clearTimeout(this.throttleTimer);
        this.update({connected: false, receivers: 0, relays: []});
    }
}

export const livePreview = new LivePreviewPublisher();
